
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/studio/DatePickerWithRange";
import { Badge } from "@/components/ui/badge";
import { Download, Filter, RefreshCw } from "lucide-react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import * as XLSX from 'xlsx';

// Sample data - In a real implementation, this would come from Supabase
const sampleReferrals = [
  { id: '1', inviter: 'john@example.com', invitee: 'alice@example.com', type: 'customer_to_customer', created_at: '2023-05-01T10:30:00Z', converted: true, bonus_given: true },
  { id: '2', inviter: 'studio1@example.com', invitee: 'bob@example.com', type: 'studio_to_customer', created_at: '2023-05-02T15:45:00Z', converted: true, bonus_given: true },
  { id: '3', inviter: 'mary@example.com', invitee: 'studio2@example.com', type: 'customer_to_studio', created_at: '2023-05-03T09:15:00Z', converted: false, bonus_given: false },
  { id: '4', inviter: 'studio1@example.com', invitee: 'studio3@example.com', type: 'studio_to_studio', created_at: '2023-05-04T14:20:00Z', converted: true, bonus_given: true },
  { id: '5', inviter: 'bob@example.com', invitee: 'charlie@example.com', type: 'customer_to_customer', created_at: '2023-05-05T11:10:00Z', converted: false, bonus_given: false },
];

type ReferralType = 'all' | 'customer_to_customer' | 'customer_to_studio' | 'studio_to_customer' | 'studio_to_studio';

export function AdminReferralExport() {
  const [isLoading, setIsLoading] = useState(false);
  const [referralType, setReferralType] = useState<ReferralType>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [referrals, setReferrals] = useState(sampleReferrals);
  const { toast } = useToast();

  const getReferralTypeLabel = (type: string) => {
    switch(type) {
      case 'customer_to_customer': return 'Kunde → Kunde';
      case 'customer_to_studio': return 'Kunde → Studio';
      case 'studio_to_customer': return 'Studio → Kunde';
      case 'studio_to_studio': return 'Studio → Studio';
      default: return type;
    }
  };

  const fetchReferrals = async () => {
    setIsLoading(true);
    
    try {
      // In a real implementation, this would fetch data from Supabase
      // const { data, error } = await supabase.from('referrals').select('*');
      // if (error) throw error;
      
      setTimeout(() => {
        // Filter by type and date range as needed
        let filtered = [...sampleReferrals];
        
        if (referralType !== 'all') {
          filtered = filtered.filter(ref => ref.type === referralType);
        }
        
        if (dateRange?.from) {
          const fromDate = dateRange.from;
          filtered = filtered.filter(ref => {
            const refDate = new Date(ref.created_at);
            return refDate >= fromDate;
          });
        }
        
        if (dateRange?.to) {
          const toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999); // End of day
          filtered = filtered.filter(ref => {
            const refDate = new Date(ref.created_at);
            return refDate <= toDate;
          });
        }
        
        setReferrals(filtered);
        setIsLoading(false);
        
        toast({
          title: "Daten aktualisiert",
          description: `${filtered.length} Referral-Einträge gefunden`,
        });
      }, 1000);
      
    } catch (error: any) {
      console.error('Error fetching referrals:', error);
      toast({
        title: "Fehler beim Laden der Daten",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const exportToExcel = () => {
    try {
      const exportData = referrals.map(ref => ({
        'Einladender': ref.inviter,
        'Eingeladener': ref.invitee,
        'Typ': getReferralTypeLabel(ref.type),
        'Datum': format(new Date(ref.created_at), 'dd.MM.yyyy HH:mm'),
        'Konvertiert': ref.converted ? 'Ja' : 'Nein',
        'Bonus vergeben': ref.bonus_given ? 'Ja' : 'Nein',
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Referrals');
      
      // Generate a timestamp for the filename
      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm');
      
      XLSX.writeFile(workbook, `chrona-referrals-${timestamp}.xlsx`);
      
      toast({
        title: "Export erfolgreich",
        description: `${exportData.length} Einträge wurden exportiert`,
      });
    } catch (error: any) {
      console.error('Error exporting data:', error);
      toast({
        title: "Fehler beim Export",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="border-chrona-gray-800 bg-chrona-gray-900/60 overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-chrona-text">Referral-Übersicht & Export</CardTitle>
        <CardDescription>
          Exportiere alle Einladungen und Empfehlungen als Excel-Datei
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <DatePickerWithRange 
              className="w-full"
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </div>
          <Select
            value={referralType}
            onValueChange={(value) => setReferralType(value as ReferralType)}
          >
            <SelectTrigger className="w-full md:w-[220px]">
              <SelectValue placeholder="Filter nach Typ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typen</SelectItem>
              <SelectItem value="customer_to_customer">Kunde → Kunde</SelectItem>
              <SelectItem value="customer_to_studio">Kunde → Studio</SelectItem>
              <SelectItem value="studio_to_customer">Studio → Kunde</SelectItem>
              <SelectItem value="studio_to_studio">Studio → Studio</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchReferrals} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Filter className="h-4 w-4 mr-2" />}
            Filtern
          </Button>
          <Button variant="default" onClick={exportToExcel} disabled={referrals.length === 0 || isLoading}>
            <Download className="h-4 w-4 mr-2" />
            Als Excel exportieren
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center p-10">
            <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Einladender</TableHead>
                  <TableHead>Eingeladener</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.length > 0 ? (
                  referrals.map((referral) => (
                    <TableRow key={referral.id}>
                      <TableCell className="font-medium">{referral.inviter}</TableCell>
                      <TableCell>{referral.invitee}</TableCell>
                      <TableCell>{getReferralTypeLabel(referral.type)}</TableCell>
                      <TableCell>{format(new Date(referral.created_at), 'dd.MM.yyyy')}</TableCell>
                      <TableCell>
                        {referral.converted ? (
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Konvertiert</Badge>
                        ) : (
                          <Badge variant="outline">Ausstehend</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      Keine Referral-Daten gefunden
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminReferralExport;
